/* Calyan Cart Aisle — top-ten data-backed smart rail plus merchant-selected collections. */
(function () {
  'use strict';

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const money = (cents) => {
    const fallback = `$${(Number(cents || 0) / 100).toFixed(2)}`;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (error) { return fallback; }
  };
  const escapeHtml = (value) => String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  const cartRailVersion = '2026-10-08';

  // These custom Shopify Customer Events measure behavior. The private line-item
  // properties added below remain the order-level source of truth.
  const publishCartRailEvent = (eventName, eventData) => {
    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(eventName, eventData);
      }
    } catch (error) {
      // Analytics must never interrupt a cart add.
    }
  };
  const railNameFor = (button) => button.closest('[data-calyan-aisle-rail]')?.dataset.calyanAisleRail || 'unknown';

  const fetchCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load the cart.');
    return response.json();
  };
  const fetchProductByHandle = async (handle) => {
    if (!handle) return null;
    const response = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
    return response.ok ? response.json() : null;
  };
  const fetchRecommendations = async (productId, intent, limit) => {
    const url = new URL(`${rootUrl()}recommendations/products.json`, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('intent', intent);
    url.searchParams.set('limit', String(Math.max(limit, 4)));
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  };
  const parseJson = (node, fallback) => { try { return JSON.parse(node?.textContent || ''); } catch (error) { return fallback; } };
  const mappingFromRoot = (root) => parseJson(root.querySelector('[data-calyan-rec-data-map]'), {});
  const productsForRail = (root, rail) => parseJson(root.querySelector(`[data-calyan-aisle-products="${rail}"]`), []);
  const fallbackProducts = (root) => parseJson(root.querySelector('[data-calyan-aisle-fallback]'), []);

  const toCandidate = (product, intent) => {
    const variant = Array.isArray(product?.variants) ? product.variants.find((entry) => entry.available) : null;
    if (!variant || !product?.available) return null;
    return {
      id: Number(product.id), handle: String(product.handle || ''), title: String(product.title || ''), url: product.url || '',
      image: product.featured_image?.url || product.featured_image || '', tags: Array.isArray(product.tags) ? product.tags : [],
      available: Boolean(product.available), variant_id: Number(variant.id), price: Number(variant.price),
      compare_at_price: Number(variant.compare_at_price || 0), intent
    };
  };

  const strategy = (value) => ({ same_scent: 'vessel_aware', cross_scent: 'different_vessel', room_spray: 'native' }[value] || value || 'vessel_aware');

  const distinctEligible = (candidates, cart) => {
    const inCart = new Set((cart.items || []).map((item) => Number(item.product_id)));
    const seen = new Set();
    return candidates.filter((candidate) => candidate && candidate.available && candidate.variant_id)
      .filter((candidate) => !inCart.has(Number(candidate.id)))
      .filter((candidate) => { if (seen.has(candidate.id)) return false; seen.add(candidate.id); return true; });
  };

  const mappedCandidatesFor = async (mapKey, map, cart) => {
    const selectedMap = map[mapKey];
    if (!selectedMap || typeof selectedMap !== 'object') return [];
    const requests = [];
    const seen = new Set();
    (cart.items || []).forEach((item) => {
      (selectedMap[String(item.product_id)] || []).forEach((entry) => {
        const handle = typeof entry === 'string' ? entry : entry?.handle;
        if (!handle || seen.has(handle)) return;
        seen.add(handle);
        requests.push({ handle, anchor_title: item.product_title || item.title || '', pair_orders_all: Number(entry?.pair_orders_all || 0), data_rank: Number(entry?.rank || 0) });
      });
    });
    const products = await Promise.all(requests.map((request) => fetchProductByHandle(request.handle)));
    return products.map((product, index) => product ? { ...toCandidate(product, `data_${mapKey}`), ...requests[index] } : null).filter(Boolean);
  };

  const smartCandidates = async (root, cart) => {
    if (strategy(root.dataset.strategy) === 'native') return [];
    const map = mappingFromRoot(root);
    const routes = strategy(root.dataset.strategy) === 'same_vessel' ? ['same_vessel_primary']
      : strategy(root.dataset.strategy) === 'different_vessel' ? ['different_vessel_fallback']
      : ['same_vessel_primary', 'different_vessel_fallback'];
    for (const route of routes) {
      const candidates = distinctEligible(await mappedCandidatesFor(route, map, cart), cart);
      if (candidates.length) return candidates.sort((a, b) => a.data_rank - b.data_rank || b.pair_orders_all - a.pair_orders_all);
    }
    return [];
  };

  const nativeCandidates = async (root, cart, limit) => {
    const source = root.dataset.source || 'smart';
    const intents = source === 'smart' ? ['complementary', 'related'] : source === 'fallback' ? [] : [source];
    const productIds = [...new Set((cart.items || []).map((item) => item.product_id).filter(Boolean))];
    const candidates = [];
    for (const intent of intents) {
      const groups = await Promise.all(productIds.map((id) => fetchRecommendations(id, intent, limit)));
      groups.flat().forEach((product) => {
        const candidate = toCandidate(product, intent);
        if (candidate) candidates.push(candidate);
      });
    }
    return distinctEligible(candidates, cart);
  };

  const pairingProof = (candidate, root) => {
    if (!String(candidate.intent || '').startsWith('data_')) return '';
    const minimum = Math.max(1, Number(root.dataset.pairingProofMinimum || 10));
    if (Number(candidate.pair_orders_all || 0) < minimum || !candidate.anchor_title) return '';
    return (root.dataset.pairingProofTemplate || 'In {count} previous orders, shoppers who chose {product} also chose:')
      .replaceAll('{count}', String(Math.round(candidate.pair_orders_all)))
      .replaceAll('{product}', candidate.anchor_title);
  };

  const activeDiscountFor = (candidate, root) => {
    const offers = mappingFromRoot(root).urgency?.active_discounts?.products?.[candidate.handle] || [];
    const now = Date.now();
    return offers.find((offer) => {
      const startsAt = offer?.starts_at ? Date.parse(offer.starts_at) : null;
      const endsAt = offer?.ends_at ? Date.parse(offer.ends_at) : null;
      return (!Number.isFinite(startsAt) || now >= startsAt) && (!Number.isFinite(endsAt) || now <= endsAt);
    }) || null;
  };

  const urgencyFor = (candidate, root) => {
    const urgency = mappingFromRoot(root).urgency || {};
    const inventory = Number(urgency.inventory_snapshot?.[candidate.handle]);
    const lowStockMax = Math.max(1, Number(root.dataset.lowStockMaximum || 5));
    if (root.dataset.showLowStock === 'true' && Number.isFinite(inventory) && inventory > 0 && inventory <= lowStockMax) {
      return { type: 'low-stock', label: `Only ${inventory} left` };
    }
    const activeDiscount = activeDiscountFor(candidate, root);
    if (activeDiscount) return { type: 'discount', label: activeDiscount.label || 'Special offer' };
    if (Number(candidate.compare_at_price || 0) > Number(candidate.price || 0)) return { type: 'sale', label: 'On sale' };
    const terms = [candidate.title, ...(candidate.tags || [])].join(' ').toLowerCase();
    if (terms.includes('limited release')) return { type: 'limited', label: 'Limited release' };
    if (urgency.momentum?.[candidate.handle]) return { type: 'popular', label: urgency.momentum[candidate.handle].label || 'Popular lately' };
    return null;
  };

  const shippingCue = (candidate, cart, root) => {
    const threshold = Number(root.dataset.shippingThreshold || 0);
    const total = Number(cart.total_price || 0);
    if (!threshold || total >= threshold || !candidate.price) return null;
    return total + Number(candidate.price) >= threshold
      ? { type: 'unlocks', text: root.dataset.shippingUnlockLabel || 'Add for free shipping' }
      : { type: 'progress', text: root.dataset.shippingProgressLabel || 'Moves you closer to free shipping' };
  };

  const renderCard = (root, candidate, cart) => {
    const template = root.querySelector('[data-calyan-aisle-product-template]');
    if (!template) return null;
    const fragment = template.content.cloneNode(true);
    const links = fragment.querySelectorAll('[data-calyan-aisle-link]');
    const image = fragment.querySelector('[data-calyan-aisle-image]');
    const title = fragment.querySelector('[data-calyan-aisle-title]');
    const price = fragment.querySelector('[data-calyan-aisle-price]');
    const badge = fragment.querySelector('[data-calyan-aisle-badge]');
    const button = fragment.querySelector('[data-calyan-rec-add]');
    links.forEach((link) => { link.href = candidate.url || '#'; });
    if (image) { image.src = candidate.image || ''; image.alt = candidate.title || ''; }
    if (title) title.textContent = candidate.title || '';
    if (price) {
      const sale = Number(candidate.compare_at_price || 0) > Number(candidate.price || 0);
      price.innerHTML = sale ? `<s>${escapeHtml(money(candidate.compare_at_price))}</s><strong>${escapeHtml(money(candidate.price))}</strong>` : `<strong>${escapeHtml(money(candidate.price))}</strong>`;
    }
    const badgeData = urgencyFor(candidate, root);
    if (badge) { badge.hidden = !badgeData; badge.textContent = badgeData?.label || ''; if (badgeData) badge.dataset.type = badgeData.type; }
    if (button) {
      button.dataset.variantId = candidate.variant_id;
      button.dataset.productId = candidate.id;
      button.dataset.productHandle = candidate.handle || '';
      button.dataset.productTitle = candidate.title || '';
      button.dataset.productPrice = Number(candidate.price || 0);
      button.setAttribute('aria-label', `Add ${candidate.title || 'this item'} to cart`);
    }
    return fragment;
  };

  const renderRail = (root, railName, candidates, cart) => {
    const rail = root.querySelector(`[data-calyan-aisle-rail="${railName}"]`);
    const track = rail?.querySelector('[data-calyan-aisle-track]');
    if (!rail || !track) return;
    const firstChild = track.firstElementChild;
    if (firstChild?.classList.contains('calyan-cart-aisle__story-card')) {
      Array.from(track.children).slice(1).forEach((element) => element.remove());
    } else {
      track.replaceChildren();
    }
    candidates.forEach((candidate) => { const card = renderCard(root, candidate, cart); if (card) track.append(card); });
    rail.hidden = !candidates.length;
  };

  const bindRails = (root) => {
    root.querySelectorAll('.calyan-cart-aisle__rail').forEach((rail) => {
      if (rail.dataset.controlsBound === 'true') return;
      rail.dataset.controlsBound = 'true';
      const track = rail.querySelector('[data-calyan-aisle-track]');
      rail.querySelector('[data-calyan-aisle-prev]')?.addEventListener('click', () => track?.scrollBy({ left: -Math.max(track.clientWidth * 0.78, 220), behavior: 'smooth' }));
      rail.querySelector('[data-calyan-aisle-next]')?.addEventListener('click', () => track?.scrollBy({ left: Math.max(track.clientWidth * 0.78, 220), behavior: 'smooth' }));
    });
  };

  const load = async (root) => {
    if (root.dataset.loading === 'true') return;
    root.dataset.loading = 'true';
    try {
      const cart = await fetchCart();
      if (!cart.items?.length) return;
      const limit = Math.max(1, Math.min(10, Number(root.dataset.limit || 10)));
      const mapped = await smartCandidates(root, cart);
      const native = mapped.length ? [] : await nativeCandidates(root, cart, limit);
      const curated = mapped.length || native.length ? [] : distinctEligible(fallbackProducts(root).map((product) => toCandidate(product, 'fallback')).filter(Boolean), cart);
      const smart = (mapped.length ? mapped : native.length ? native : curated).slice(0, limit);
      renderRail(root, 'smart', smart, cart);
      renderRail(root, 'gift', distinctEligible(productsForRail(root, 'gift').map((product) => toCandidate(product, 'gift')).filter(Boolean), cart).slice(0, limit), cart);
      renderRail(root, 'spray', distinctEligible(productsForRail(root, 'spray').map((product) => toCandidate(product, 'spray')).filter(Boolean), cart).slice(0, limit), cart);
      const smartHeading = root.querySelector('[data-calyan-aisle-smart-heading]');
      if (smartHeading && smart.length) smartHeading.textContent = pairingProof(smart[0], root) || root.dataset.smartHeading || 'Shoppers who paired this also chose';
      root.__calyanCart = cart;
      bindRails(root);
      root.hidden = !root.querySelector('.calyan-cart-aisle__rail:not([hidden])');
      restoreAislePosition();
    } catch (error) {
      root.hidden = true;
    } finally { root.dataset.loading = 'false'; }
  };

  const initialize = (scope = document) => {
    const roots = scope.matches?.('[data-calyan-cart-recommendations]') ? [scope] : Array.from(scope.querySelectorAll?.('[data-calyan-cart-recommendations]') || []);
    roots.forEach((root) => { if (root.dataset.initialized === 'true') return; root.dataset.initialized = 'true'; load(root); });
  };

  const captureAislePosition = (button) => {
    const root = button.closest('[data-calyan-cart-recommendations]');
    const body = document.querySelector('.wt-cart__drawer__body');
    return {
      bodyTop: body?.scrollTop || 0,
      rails: Array.from(root?.querySelectorAll('[data-calyan-aisle-rail]') || []).map((rail) => ({
        name: rail.dataset.calyanAisleRail,
        left: rail.querySelector('[data-calyan-aisle-track]')?.scrollLeft || 0,
      })),
    };
  };

  const restoreAislePosition = () => {
    const saved = window.__calyanCartAislePosition;
    if (!saved) return;
    const body = document.querySelector('.wt-cart__drawer__body');
    if (body) body.scrollTop = Number(saved.bodyTop || 0);
    const root = document.querySelector('[data-calyan-cart-recommendations]');
    saved.rails?.forEach(({ name, left }) => {
      const track = root?.querySelector(`[data-calyan-aisle-rail="${name}"] [data-calyan-aisle-track]`);
      if (track) track.scrollLeft = Number(left || 0);
    });
    window.__calyanCartAislePosition = null;
  };

  const showAddedConfirmation = (root, title) => {
    document.querySelector('.calyan-cart-aisle__toast')?.remove();
    const toast = document.createElement('p');
    toast.className = 'calyan-cart-aisle__toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = `<span aria-hidden="true">✓</span><span>${escapeHtml(root?.dataset.addedMessage || 'Added to cart')}${title ? `: ${escapeHtml(title)}` : ''}</span>`;
    document.body.append(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 260);
    }, 1900);
  };

  const addRecommendation = async (button) => {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;
    const cartDrawer = document.querySelector('cart-drawer');
    const root = button.closest('[data-calyan-cart-recommendations]');
    const rail = railNameFor(button);
    const productId = Number(button.dataset.productId || 0);
    const productTitle = button.dataset.productTitle || button.getAttribute('aria-label')?.replace(/^Add\s+/i, '') || '';
    const productHandle = button.dataset.productHandle || '';
    const productPrice = Number(button.dataset.productPrice || 0);
    window.__calyanCartAislePosition = captureAislePosition(button);
    button.disabled = true;
    button.classList.add('is-adding');
    const data = new FormData();
    data.append('id', variantId); data.append('quantity', '1'); data.append('sections_url', window.location.pathname);
    data.append('properties[_calyan_cart_rail]', rail);
    data.append('properties[_calyan_cart_rail_version]', cartRailVersion);
    if (cartDrawer?.getSectionsToRender) {
      const sections = cartDrawer.getSectionsToRender().map((section) => section.section || section.id).filter(Boolean);
      if (sections.length) data.append('sections', [...new Set(sections)].join(','));
    }
    try {
      const response = await fetch(`${rootUrl()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data });
      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(payload.description || 'Unable to add this item.');
      const addedPriceCents = Number(payload.final_price ?? payload.price ?? productPrice);
      const currency = window.Shopify?.currency?.active || document.documentElement.getAttribute('data-currency') || 'USD';
      publishCartRailEvent('calyan:cart_rail_added', {
        cart_rail: rail,
        cart_rail_version: cartRailVersion,
        product_id: productId ? String(productId) : '',
        variant_id: String(variantId),
        product_handle: productHandle,
        product_title: productTitle,
        quantity: 1,
        rail_value: addedPriceCents / 100,
        currency,
      });
      showAddedConfirmation(root, productTitle);
      if (cartDrawer?.refreshCartDrawer) await cartDrawer.refreshCartDrawer();
      else if (cartDrawer?.renderContents && payload.sections) cartDrawer.renderContents(payload, false);
      else window.location.assign(`${rootUrl()}cart`);
      window.setTimeout(() => initialize(document), 0);
    } catch (error) {
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = error.message || 'Unable to add this item. Please try again.';
      button.disabled = false;
      button.classList.remove('is-adding');
    }
  };

  document.addEventListener('click', (event) => { const button = event.target.closest('[data-calyan-rec-add]'); if (button) { event.preventDefault(); addRecommendation(button); } });
  const refresh = () => window.setTimeout(() => {
    const roots = Array.from(document.querySelectorAll('[data-calyan-cart-recommendations]'));
    roots.forEach((root) => load(root));
    initialize(document);
  }, 0);
  document.addEventListener('calyan:cart-rendered', refresh);
  document.addEventListener('cart:updated', refresh);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('DOMContentLoaded', () => initialize(document));
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
  if (document.readyState !== 'loading') initialize(document);
}());
